/**
 * @fileoverview Section for Sync Settings on the Specify Listings Configuration page
 *
 * @author Heesun Jun (hjun@yext.com)
 */

import {DeliveryConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/data/deliveryconfiguration';
import {Checkbox} from '/ui/components/checkbox/checkbox';
import {Table} from '/ui/components/table/table';
import {Tooltip} from '/ui/components/tooltip/tooltip';

import * as styles from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/specifylistingconfiguration.module.scss';

const GOOGLE_PUBLISHER_ID = 715;
const FACEBOOK_PUBLISHER_ID = 559;
const EXTENDED_NETWORK_PUBLISHER_ID = 0;

/**
 * @param {{
 *  syncSettings: !Map<number, DeliveryConfiguration>,
 *  setSyncSettings: function(!Map<number, DeliveryConfiguration>): void,
 *  publisherIds: Array<number>,
 * }} props
 * @return {React.ReactNode}
 */

export function SyncSettings({syncSettings, setSyncSettings, publisherIds}) {
  const COLUMN_IDS = {
    LISTINGSSYNCSETTINGS: 'listingsSyncSettings',
    GOOGLE: 'google',
    FACEBOOK: 'facebook',
    EXTENDED: 'extended',
  };

  const settingsColumns = [
    {
      id: COLUMN_IDS.LISTINGSSYNCSETTINGS,
      label: <TooltipContainer
        unclearText={yext.msg('Listings Sync Settings')}
        tooltipText={yext.msg('Use these settings to require manual approval before syncing data to Listings.')}
      />,
      width: '240px',
    },
    {
      id: COLUMN_IDS.GOOGLE,
      label: yext.msg('Google Business Profile'),
      width: '240px',
    },
    {
      id: COLUMN_IDS.FACEBOOK,
      label: yext.msg('Facebook'),
      width: '240px',
    },
    {
      id: COLUMN_IDS.EXTENDED,
      label: yext.msg('Extended Network'),
      width: '240px',
    },
  ];
  const googleConfig = syncSettings.get(GOOGLE_PUBLISHER_ID);
  const fbConfig = syncSettings.get(FACEBOOK_PUBLISHER_ID);
  const config = syncSettings.get(EXTENDED_NETWORK_PUBLISHER_ID);

  const settingsDataRows = [
    {
      id: 'listings-updates-selector',
      [COLUMN_IDS.LISTINGSSYNCSETTINGS]: <TooltipContainer
        unclearText={yext.msg('Disable Listings Updates')}
        tooltipText={yext.msg('This setting means we will not sync data to existing listings on the selected publishers until manually approved.')}
      />,
      [COLUMN_IDS.GOOGLE]: <Checkbox
        id="google-listings-update"
        value={!!googleConfig?.dontSyncListings}
        checked={!!googleConfig?.dontSyncListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontSyncListings(syncSettings, [GOOGLE_PUBLISHER_ID], e.target.checked))}
      />,
      [COLUMN_IDS.FACEBOOK]: <Checkbox
        id="facebook-listings-update"
        value={!!fbConfig?.dontSyncListings}
        checked={!!fbConfig?.dontSyncListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontSyncListings(syncSettings, [FACEBOOK_PUBLISHER_ID], e.target.checked))}
      />,
      [COLUMN_IDS.EXTENDED]: <Checkbox
        id="extended-listings-update"
        value={!!config?.dontSyncListings}
        checked={!!config?.dontSyncListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontSyncListings(syncSettings, [EXTENDED_NETWORK_PUBLISHER_ID], e.target.checked))}
      />,
    },
    {
      id: 'listings-creation-selector',
      [COLUMN_IDS.LISTINGSSYNCSETTINGS]: <TooltipContainer
        unclearText={yext.msg('Disable Listings Creation')}
        tooltipText={yext.msg('This setting means no listings will be created on the selected publishers until manually approved.')}
      />,
      [COLUMN_IDS.GOOGLE]: <Checkbox
        id="google-listings-creation"
        value={!!googleConfig?.dontCreateNewListings}
        checked={!!googleConfig?.dontCreateNewListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontCreateNewListings(syncSettings, [GOOGLE_PUBLISHER_ID], e.target.checked))}
      />,
      [COLUMN_IDS.FACEBOOK]: <Checkbox
        id="facebook-listings-creation"
        value={!!fbConfig?.dontCreateNewListings}
        checked={!!fbConfig?.dontCreateNewListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontCreateNewListings(syncSettings, [FACEBOOK_PUBLISHER_ID], e.target.checked))}
      />,
      [COLUMN_IDS.EXTENDED]: <Checkbox
        id="extended-listings-creation"
        value={!!config?.dontCreateNewListings}
        checked={!!config?.dontCreateNewListings}
        onChange={e => setSyncSettings(DeliveryConfiguration.withDontCreateNewListings(syncSettings, [EXTENDED_NETWORK_PUBLISHER_ID], e.target.checked))}
        disabled={true}
      />,
    },
  ];

  return (
    <div className="configuration-section">
      <Table
        label={yext.msg('Listings Sync Settings')}
        columns={settingsColumns}
        dataRows={settingsDataRows}
      />
    </div>
  );
}

function TooltipContainer({unclearText, tooltipText}) {
  return (
    <div className={styles.tooltipContainer}><Tooltip unclearText={unclearText} tooltipText={tooltipText} /></div>
  );
}
